import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import Input from '../components/Input';
import { useCartStore } from '../store/cartStore';
import { formatDateLongInTimezone, formatTimeInTimezone, getBrowserTimezone } from '../utils/timezone';

interface TimeSlot {
  windowId: string;
  windowName?: string;
  windowType: 'daily' | 'weekly' | 'monthly';
  startDateTime: string;
  endDateTime: string;
  price: number;
  availableQuantity: number;
  totalQuantity: number;
}

interface RenterDetailRequirement {
  fieldName: string;
  description: string;
  required: boolean;
}

interface Listing {
  _id: string;
  name: string;
  title: string;
  timezone: string;
  renterDetailRequirements: RenterDetailRequirement[];
}

const ReservationDetailPage: React.FC = () => {
  const { listingName, windowId, date } = useParams<{
    listingName: string;
    windowId: string;
    date: string;
  }>();
  const navigate = useNavigate();
  const { addItem } = useCartStore();
  const [searchParams] = useSearchParams();

  const [listing, setListing] = useState<Listing | null>(null);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBooking, setIsBooking] = useState(false);
  
  const [selectedStartSlot, setSelectedStartSlot] = useState<string>('');
  const [selectedEndSlot, setSelectedEndSlot] = useState<string>('');
  const [renterDetails, setRenterDetails] = useState<Record<string, string>>({});

  // Every slot's start/end instant means whatever it means in the listing's
  // timezone, not the renter's browser timezone — falling back to the
  // browser's own zone only guards against unexpectedly missing data, since
  // `timezone` is a required field on every listing.
  const timezone = listing?.timezone || getBrowserTimezone();

  // Fetch listing and slots
  const fetchData = useCallback(async () => {
    if (!listingName || !date) return;

    setIsLoading(true);
    try {
      // Fetch listing
      const listingRes = await api.get<{ listing: Listing }>(`/listings/name/${listingName}`);
      setListing(listingRes.listing);

      // Fetch slots for the date
      const slotsRes = await api.get<{ slots: TimeSlot[] }>(
        `/slots/listing/name/${listingName}/date/${date}`
      );
      
      // Filter to only the specified window
      const windowSlots = slotsRes.slots.filter(slot => slot.windowId === windowId);
      setSlots(windowSlots);

      // Try to use the startTime from query parameter if available
      const startTimeParam = searchParams.get('startTime');
      let initialStartSlot = windowSlots[0]; // Fallback to first slot
      
      if (startTimeParam) {
        // Find the slot that matches the query parameter
        const matchingSlot = windowSlots.find(slot => slot.startDateTime === startTimeParam);
        if (matchingSlot) {
          initialStartSlot = matchingSlot;
        }
      }

      // Set initial start and end time
      if (initialStartSlot) {
        setSelectedStartSlot(initialStartSlot.startDateTime);
        setSelectedEndSlot(initialStartSlot.endDateTime);
      }
    } catch {
      toast.error('Failed to load reservation details');
      navigate(`/${listingName}`);
    } finally {
      setIsLoading(false);
    }
  }, [listingName, date, windowId, navigate, searchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get available end times based on selected start time
  const availableEndTimes = useCallback(() => {
    if (!selectedStartSlot || slots.length === 0) return [];

    const startSlotIndex = slots.findIndex(s => s.startDateTime === selectedStartSlot);
    if (startSlotIndex === -1) return [];

    const endTimes: Array<{ value: string; label: string; price: number }> = [];
    
    // First option is the end of the first slot
    const firstSlot = slots[startSlotIndex];
    endTimes.push({
      value: firstSlot.endDateTime,
      label: formatTimeInTimezone(new Date(firstSlot.endDateTime), timezone),
      price: firstSlot.price,
    });

    // Check for consecutive slots
    let totalPrice = firstSlot.price;
    for (let i = startSlotIndex + 1; i < slots.length; i++) {
      const prevSlot = slots[i - 1];
      const currentSlot = slots[i];

      // Check if slots are consecutive (no gap)
      if (new Date(prevSlot.endDateTime).getTime() !== new Date(currentSlot.startDateTime).getTime()) {
        break; // Gap found, can't extend further
      }

      // Check if current slot is available
      if (currentSlot.availableQuantity === 0) {
        break; // Not available, can't extend
      }

      totalPrice += currentSlot.price;
      endTimes.push({
        value: currentSlot.endDateTime,
        label: formatTimeInTimezone(new Date(currentSlot.endDateTime), timezone),
        price: totalPrice,
      });
    }

    return endTimes;
  }, [selectedStartSlot, slots, timezone]);

  // Calculate total price
  const totalPrice = useCallback(() => {
    const endTimeOption = availableEndTimes().find(et => et.value === selectedEndSlot);
    return endTimeOption ? endTimeOption.price : 0;
  }, [selectedEndSlot, availableEndTimes]);

  const handleAddToCart = async () => {
    if (!listing || !selectedStartSlot || !selectedEndSlot) {
      toast.error('Please select start and end times');
      return;
    }

    // Validate required fields
    const missingFields = listing.renterDetailRequirements
      .filter(req => req.required && !renterDetails[req.fieldName]?.trim())
      .map(req => req.fieldName);

    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }

    setIsBooking(true);
    try {
      await addItem({
        listingId: listing._id,
        windowId: windowId!,
        startDateTime: new Date(selectedStartSlot),
        endDateTime: new Date(selectedEndSlot),
        price: totalPrice(),
        renterDetails,
      });

      toast.success('Added to cart!');
      navigate(`/${listingName}`);
    } catch {
      toast.error('Failed to add to cart');
    } finally {
      setIsBooking(false);
    }
  };

  const handleCancel = () => {
    navigate(`/${listingName}`);
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto py-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  if (!listing || slots.length === 0) {
    return (
      <div className="max-w-2xl mx-auto py-8">
        <div className="text-center text-gray-600">
          <p className="mb-4">No available slots found</p>
          <Button onClick={handleCancel}>Go Back</Button>
        </div>
      </div>
    );
  }

  const selectedDateObj = date ? new Date(date) : new Date();

  return (
    <div className="max-w-2xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{listing.title}</h1>
        <p className="text-lg text-gray-600 mb-6">{formatDateLongInTimezone(selectedDateObj, 'UTC')}</p>

        {/* Start Time Selector */}
        <div className="mb-6">
          <label htmlFor="start-time" className="block text-sm font-medium text-gray-700 mb-2">
            Start Time
          </label>
          <select
            id="start-time"
            value={selectedStartSlot}
            onChange={(e) => {
              setSelectedStartSlot(e.target.value);
              // Reset end time to the end of the new start slot
              const newStartSlot = slots.find(s => s.startDateTime === e.target.value);
              if (newStartSlot) {
                setSelectedEndSlot(newStartSlot.endDateTime);
              }
            }}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            {slots.map((slot) => (
              <option key={slot.startDateTime} value={slot.startDateTime}>
                {formatTimeInTimezone(new Date(slot.startDateTime), timezone)}
                {slot.availableQuantity === 0 && ' (Not Available)'}
              </option>
            ))}
          </select>
        </div>

        {/* End Time / Duration Selector */}
        <div className="mb-6">
          <label htmlFor="end-time" className="block text-sm font-medium text-gray-700 mb-2">
            End Time
          </label>
          <select
            id="end-time"
            value={selectedEndSlot}
            onChange={(e) => setSelectedEndSlot(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            {availableEndTimes().map((endTime) => (
              <option key={endTime.value} value={endTime.value}>
                {endTime.label} (${endTime.price.toFixed(2)})
              </option>
            ))}
          </select>
        </div>

        {/* Total Price */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-lg font-medium text-gray-900">Total Price:</span>
            <span className="text-2xl font-bold text-primary-600">
              ${totalPrice().toFixed(2)}
            </span>
          </div>
        </div>

        {/* Renter Details */}
        {listing.renterDetailRequirements.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
            <div className="space-y-4">
              {listing.renterDetailRequirements.map((req) => (
                <div key={req.fieldName}>
                  <Input
                    id={`renter-detail-${req.fieldName}`}
                    label={req.fieldName}
                    value={renterDetails[req.fieldName] || ''}
                    onChange={(e) =>
                      setRenterDetails({ ...renterDetails, [req.fieldName]: e.target.value })
                    }
                    required={req.required}
                    placeholder={req.description}
                  />
                  {req.description && (
                    <p className="mt-1 text-sm text-gray-500">{req.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={handleCancel}
            variant="secondary"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddToCart}
            isLoading={isBooking}
            disabled={isBooking || !selectedStartSlot || !selectedEndSlot}
            className="flex-1"
          >
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReservationDetailPage;

